/**
 * FeatureFlagsPage — admin feature-flags management (STORY-008-009). An
 * `AdminTable` of flags with an inline enable toggle, a "New flag" action, and a
 * row-activated `FlagFormDialog` (edit / delete). Backed by the SHIPPED
 * STORY-008-005 router. Toggling sends the FULL PUT body (the backend replaces
 * the row), preserving description + rules. RBAC: the route already gates on
 * `ops.feature_flags.manage` (read == write), so no button-level gating here.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminTable } from "@/features/admin/components/AdminTable";
import { FlagFormDialog } from "@/features/admin/feature-flags/FlagFormDialog";
import { useFlags, useUpdateFlag } from "@/features/admin/feature-flags/hooks";
import type { AdminFeatureFlag } from "@/features/admin/feature-flags/api";

export function FeatureFlagsPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const { data, isLoading, isError } = useFlags();
  const update = useUpdateFlag();

  const [dialog, setDialog] = useState<
    { mode: "create"; initial: null } | { mode: "edit"; initial: AdminFeatureFlag } | null
  >(null);

  const rows: AdminFeatureFlag[] = useMemo(() => data?.items ?? [], [data]);

  const toggle = (flag: AdminFeatureFlag): void => {
    void update.mutate({
      key: flag.key,
      body: {
        enabled: !flag.enabled,
        description: flag.description,
        rules: flag.rules,
      },
    });
  };

  const columns = [
    { key: "key", label: t("featureFlags.columns.key") },
    { key: "enabled", label: t("featureFlags.columns.enabled") },
    { key: "rules", label: t("featureFlags.columns.rules") },
    { key: "updated", label: t("featureFlags.columns.updated") },
  ] as const;

  const toggleButton = (flag: AdminFeatureFlag): ReactElement => (
    <Button
      variant={flag.enabled ? "primary" : "outline"}
      size="sm"
      data-testid={`flag-toggle-${flag.key}`}
      aria-pressed={flag.enabled}
      aria-label={t("featureFlags.toggleAria", { key: flag.key })}
      onClick={(e) => {
        e.stopPropagation();
        toggle(flag);
      }}
    >
      {flag.enabled ? t("featureFlags.on") : t("featureFlags.off")}
    </Button>
  );

  const renderCell = (
    flag: AdminFeatureFlag,
    key: string,
  ): ReactElement | string => {
    switch (key) {
      case "key":
        return (
          <span className="font-medium">
            {flag.key}
            {flag.description ? (
              <span className="mt-0.5 block text-xs font-normal text-[var(--g-secondary)]">
                {flag.description}
              </span>
            ) : null}
          </span>
        );
      case "enabled":
        return toggleButton(flag);
      case "rules":
        return flag.rules.length === 0 ? (
          <GPill tone="neutral" size="sm">
            {t("featureFlags.allUsers")}
          </GPill>
        ) : (
          <span className="flex flex-wrap gap-1">
            {flag.rules.map((r, i) => (
              <GPill key={i} tone="accent" size="sm">
                {r.attribute}: {r.values.join(", ")}
              </GPill>
            ))}
          </span>
        );
      case "updated":
        return (
          <span className="text-[var(--g-secondary)]">
            {new Date(flag.updated_at).toLocaleDateString()}
          </span>
        );
      default:
        return "";
    }
  };

  const renderMobileCard = (flag: AdminFeatureFlag): ReactElement => (
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="font-medium">{flag.key}</div>
        {flag.description ? (
          <div className="mt-0.5 text-xs text-[var(--g-secondary)]">
            {flag.description}
          </div>
        ) : null}
        <div className="mt-1 flex flex-wrap gap-1">
          {flag.rules.length === 0 ? (
            <GPill tone="neutral" size="sm">
              {t("featureFlags.allUsers")}
            </GPill>
          ) : (
            flag.rules.map((r, i) => (
              <GPill key={i} tone="accent" size="sm">
                {r.attribute}: {r.values.join(", ")}
              </GPill>
            ))
          )}
        </div>
      </div>
      {toggleButton(flag)}
    </div>
  );

  return (
    <div data-testid="admin-feature-flags-page" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold">{t("featureFlags.title")}</h1>
        <Button
          variant="primary"
          size="md"
          data-testid="flag-new"
          onClick={() => setDialog({ mode: "create", initial: null })}
        >
          {t("featureFlags.new")}
        </Button>
      </div>

      <AdminTable<AdminFeatureFlag>
        columns={columns}
        rows={rows}
        rowKey={(f) => f.key}
        renderCell={renderCell}
        renderMobileCard={renderMobileCard}
        onRowActivate={(f) => setDialog({ mode: "edit", initial: f })}
        rowActivation="cell"
        rowAriaLabel={(f) => f.key}
        testid="admin-feature-flags"
        isLoading={isLoading}
        isError={isError}
        emptyLabel={t("featureFlags.empty")}
        errorLabel={t("error.loadFailed")}
      />

      <FlagFormDialog
        open={dialog !== null}
        mode={dialog?.mode ?? "create"}
        initial={dialog?.mode === "edit" ? dialog.initial : null}
        onClose={() => setDialog(null)}
        onSuccess={() => setDialog(null)}
      />
    </div>
  );
}

export default FeatureFlagsPage;
